import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getAdvisor, getAdvisorMeetings, getAdvisorMeeting, getCalendlyMeetingDetails } from "../../../core/services/gutcheck-api.service";
import { IAdvisor, IScheduler, SchedulerType } from "../../../types";
import { CalendlyScheduler } from "../components/CalendlyScheduler";
import { MeetingLinks } from "../components/CalendlyScheduler";
import { useDealContext } from "../../deal";
import { Box, TableContainer, TableHead, TableRow, TableCell, TableBody, Typography, Rating } from "@mui/material";
import { AdvisorTable } from "../../company/styles/AdvisorStyles";
import { DateDisplay } from "../components/DateDisplay";
import useAnalytics from "../../../hooks/useAnalytics";
import Loading from "../../../components/Loading";
import { DocumentTitle } from "../../../components/DocumentTitle";
import { useSchedulerByType } from "../../../hooks";
import { RATING_LABELS } from "../../../constants/ratings";

export const Schedule = () => {
    const { dealContext, setDealContext } = useDealContext();
    const [advisor, setAdvisor] = useState<IAdvisor | null>(null);
    const [project, setProject] = useState<any | null>(null);
    const { advisorPublicId, schedulerType = "intro" } = useParams();
    const { trackAdvisorPage } = useAnalytics();
    const [meetings, setMeetings] = useState<any[]>([]);
    const [meetingDetails, setMeetingDetails] = useState<any | null>(null);
    const [selectedMeeting, setSelectedMeeting] = useState<any | null>(null);
    const [isDemoSchedulerOpen, setIsDemoSchedulerOpen] = useState(false);

    // Use the new hook to fetch scheduler
    const { scheduler, loading: schedulerLoading } = useSchedulerByType({
        projectTag: project?.tag || "",
        type: schedulerType as SchedulerType,
        isPublic: true,
        enabled: !!(advisor && project)
    });

    useEffect(() => {
        const fetchAdvisor = async () => {
            if (advisorPublicId) {
                const { data, error } = await getAdvisor(advisorPublicId);
                if (data) {
                    setAdvisor(data.advisor);
                    setProject(data.advisor.survey_link.project);
                }
            }
        }

        if (advisorPublicId) {
            fetchAdvisor();
        }
    }, [advisorPublicId]);

    useEffect(() => {
        if (advisor) {
            fetchMeetings();
            trackAdvisorPage(advisor, 'Advisor - Public Scheduler');
        }
    }, [advisor]);

    useEffect(() => {
        if (project) {
            setDealContext({
                ...dealContext,
                project: project,
                organization: project.organization,
                surveyLink: project.survey_link,
                role: "guest"
            });
        }
    }, [project]);

    const fetchMeetingDetails = async () => {
        if (advisor?.prospect_event_invitee_uri) {
            const inviteeId = advisor.prospect_event_invitee_uri.split("/invitees/")[1];
            const { data, error } = await getCalendlyMeetingDetails(advisor.public_id, inviteeId);
            if (data) {
                if (data.invitee.canceled && !data.invitee.rescheduled) {
                    setMeetingDetails(null);
                } else {
                    setMeetingDetails(data.invitee);
                }
            } else {
                console.error('Error fetching meeting details:', error);
            }
        }
    };

    const fetchMeetings = async () => {
        if (advisor) {
            const { data, error } = await getAdvisorMeetings(advisor.public_id);
            if (data) {
                setMeetings(data.meetings.filter((meeting: any) => new Date(meeting.event_start) > new Date()));
            }
        }
    }

    const handleMeetingEvent = async (e: any, meetingUuid: string) => {
        if (!advisor) {
            return;
        }
        const { data, error } = await getAdvisorMeeting(advisor.public_id, meetingUuid);
        if (data) {
            const meeting = data.meeting;
            setMeetings(meetings.map((m: any) => m.id === meeting.id ? meeting : m));
        }
    }

    if (!advisor || !project || !scheduler) {
        return <Loading />;
    }

    return (
        <div className="page-main" id="evaluation-content">
            <DocumentTitle pageTitle={`${project.name} ${project.effective_scheduler_properties?.[schedulerType]?.name}: ${advisor.user?.profile?.name || advisor.name}`} />
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    bgcolor: 'background.default',
                    width: '100%',
                    maxWidth: '1200px',
                    flexDirection: 'column',
                    alignItems: 'center',
                    margin: "40px auto",

                }}
            >
                <h3 style={{ textAlign: 'center' }}>{project.name} {project.effective_scheduler_properties?.[schedulerType]?.name}: {advisor.user?.profile?.name || advisor.name}</h3>
                <Typography variant="body1" style={{ textAlign: 'center' }}>
                    Use this page to schedule a meeting between {advisor.user?.profile?.name || advisor.name} and {advisor.survey_link.project.name}
                </Typography>

                {meetings?.length > 0 && (
                    <TableContainer sx={{ marginTop: '30px', padding: "0 40px" }}>
                        <p><strong>Upcoming meetings</strong></p>
                        <AdvisorTable>
                            <TableHead>
                                <TableRow>
                                    <TableCell className="center">
                                        Date
                                    </TableCell>
                                    <TableCell>
                                        Time
                                    </TableCell>
                                    <TableCell>
                                        Description
                                    </TableCell>
                                    <TableCell>
                                        Status
                                    </TableCell>
                                    <TableCell className="center">
                                        Action
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {meetings?.map((meeting: any) => (
                                    <TableRow key={meeting.uuid}>
                                        <TableCell className="center">
                                            <DateDisplay meeting={meeting} />
                                        </TableCell>
                                        <TableCell>
                                            {new Date(meeting.event_start).toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' })}
                                        </TableCell>
                                        <TableCell>
                                            {meeting.description}
                                        </TableCell>

                                        <TableCell>
                                            {meeting.status.charAt(0).toUpperCase() + meeting.status.slice(1).toLowerCase()}
                                        </TableCell>
                                        <TableCell className="center">
                                            {(meeting.status === "completed" || new Date(meeting.event_start) < new Date()) && (
                                                <Box sx={{
                                                    minHeight: '60px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer',
                                                    flexDirection: 'column',
                                                    gap: '5px'
                                                }}
                                                    onClick={() => setSelectedMeeting(meeting)}>
                                                    <Rating
                                                        value={meeting.ratings?.length > 0 ? meeting.ratings[0].rating : null}
                                                        max={4}
                                                        readOnly
                                                    />
                                                    <Typography component="legend" variant="body2">
                                                        {meeting.ratings?.length > 0 ? (
                                                            RATING_LABELS[meeting.ratings[0].rating]
                                                        ) : "Please rate the meeting"}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {(meeting.status === "scheduled" && new Date(meeting.event_start) > new Date()) && <MeetingLinks advisor={advisor} meeting={meeting} handleMeetingEvent={handleMeetingEvent} fetchMeetingDetails={fetchMeetingDetails} />}
                                            {meeting.status === "requested" && <button onClick={() => setIsDemoSchedulerOpen(true)}>Schedule a Demo</button>}

                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </AdvisorTable>
                    </TableContainer>
                )}

                <CalendlyScheduler project={project} scheduler={scheduler} advisor={advisor} onMeetingCreated={fetchMeetings} />
            </Box>
        </div>
    )
}